// What a project's app host answers (ADR 0021): its contract version, what its web page reads, how far a person
// reaches the app, a person of the app, and the host's own refusals. Plain types and one error class; cortex's api
// re-exports them (@cortex/api-kit).
import type { AccessLevel, Role } from '@cortex/access-policy';

/**
 * The contract version: `cortex-app-host/<major of @cortex/app-host>`. A host refuses a plugin of another contract.
 * This host (HostInstance, no design version: app-host 4) speaks `cortex-app-host/4`, the baseline (ADR 0061, Board
 * #2699).
 */
export const APP_HOST_CONTRACT = 'cortex-app-host/4' as const;

/** The shell's page of a person's own sign-in security (passkeys, an authenticator app). */
export const ACCOUNT_SECURITY_PAGE = '/account/security';

/** What the app's web page reads (GET /__app/info, and the page's own global). */
export interface AppInfo {
  contract: typeof APP_HOST_CONTRACT;
  project: { slug: string; name: string };
  /** The project's apps (apps/<key>/app.json): their keys and titles, in order. */
  apps: { key: string; title: string }[];
  /** The app the page opens on (and signing in lands on): the host's `home`, else the first; null: no app. */
  home: string | null;
  environment: string;
  sdks: { module: string; package: string; version: string; companion: string | null }[];
  branding: { name: string; primaryColor: string | null; icon: string };
  /**
   * The sign-in: the provider's name, straight there or not, where it starts and where signing out goes; `security`,
   * the person's own sign-in security routes (passkeys, an authenticator app) when the provider has them, else null
   * (absent: a host older than them).
   */
  signIn: {
    provider: string;
    auto: boolean;
    start: string;
    signOut: string;
    security?: string | null;
  } | null;
  /** The app's own version: the commit of its repo (never cortex's). */
  app: { commit: string | null };
}

/** How far a caller reaches the project: the identity provider's role, mapped (admin/member/viewer). */
export interface AppAccess {
  /** The role the data-access policies see. */
  role: Role;
  level: AccessLevel;
  /** A project manager (the provider's `admin`). */
  canManage: boolean;
}

/** A person of the app: the stable id the SDKs key per-person rows by, the email, the display name. */
export interface AppPerson {
  id: string;
  subject: string;
  email: string;
  name: string | null;
}

/** An error of the app host (and of an SDK's `./app` adapter) that the SDKs answer with its status. */
export class AppHostError extends Error {
  override readonly name = 'AppHostError';
  readonly statusCode: 400 | 401 | 403 | 404 | 409 | 503;
  readonly code: string;
  constructor(statusCode: 400 | 401 | 403 | 404 | 409 | 503, code: string, message: string) {
    super(message);
    this.statusCode = statusCode;
    this.code = code;
  }
}

/** Maps the host's own errors to a response (null: not one of its errors). */
export type HostErrorPort = (err: unknown) => { status: number; body: unknown } | null;
